'use client';
import { useGSAP } from '@gsap/react';
import { useRef, useState, useEffect } from 'react';
import { Box, Grid, Stack, Text, VStack } from '@chakra-ui/react';

import BlockDiagonalDivider from 'shared/BlockDiagonalDivider';
import { aboutProgramAnimation } from 'shared/gsap';
import BlockShadow from 'shared/BlockShadow';
import NextImage from 'shared/NextImage';
import InterText from 'shared/InterText';
import { isSafari } from 'shared/lib';

import { SECTIONS_IDS } from 'constants/global';
import Bg from 'assets/illustrations/aboutProgram/blockBg.webp';
import { bottomShadowSameStyles, cardsData } from 'constants/aboutProgram';

const AboutProgram = () => {
  const cardsContainerRef = useRef(null);
  useGSAP(aboutProgramAnimation(cardsContainerRef));

  // Нужно для решения проблемы что safari уменьшает breakpoint'ы на 10 px
  const [mobileBreakpoint, setMobileBreakpoint] = useState('xt');
  useEffect(() => {
    if (isSafari()) setMobileBreakpoint('safariXt');
  }, []);

  return (
    <Box
      as="section"
      id={SECTIONS_IDS.aboutProgram}
      w="full"
      minH={{ mobile: '515px', tablet: 'clamp(572px, 56vw, 600px)', laptop: '737px' }}
      alignItems="start"
      pos="relative"
      overflow="hidden"
      color="ourWhite"
    >
      <BlockDiagonalDivider position="top" direction="left" zIndex="2" />

      <Box
        w="full"
        minW={{ mobile: '615px', tablet: 'unset' }}
        top={{ mobile: '0', [mobileBreakpoint]: 'unset' }}
        bottom={{ [mobileBreakpoint]: '0', laptop: '-215px' }}
        left={{ mobile: 'calc(50% - (615px / 2))', tablet: '0' }}
        pos="absolute"
        zIndex="1"
      >
        <NextImage src={Bg} alt="Фоновое изображение" w="full" h="full" />
        {/* @ts-ignore */}
        <BlockShadow {...bottomShadowSameStyles} hideFrom={mobileBreakpoint} />
      </Box>

      <Grid w="full" templateColumns="1fr" templateRows="1fr" zIndex="3">
        <Text
          as="h2"
          id="aboutProgram-title"
          gridArea="1 / 1"
          w="full"
          p={{ mobile: '165px 16px 0', tablet: '230px 30px 0', laptop: '286px 100px 0' }}
          textAlign="center"
          textTransform="uppercase"
          zIndex="3"
        >
          Траектория лидерства — прямой путь к роли руководителя
        </Text>

        <Text
          id="aboutProgram-text"
          gridArea="1 / 1"
          w="full"
          p={{ mobile: '195px 16px 0', tablet: '230px 22px 0', laptop: '327px 210px 0' }}
          textAlign="center"
          textStyle="h5"
          opacity="0"
          zIndex="2"
        >
          Настоящие проекты и реальные задачи бизнеса. Учись на опыте ASTON
        </Text>

        <Box
          id="aboutProgram-cards"
          gridArea="1 / 1"
          p={{ mobile: '63px 16px 50px', tablet: '120px 20px 0', laptop: '158px 80px 0' }}
          zIndex="1"
          overflow="hidden"
          opacity="0"
        >
          <Stack
            ref={cardsContainerRef}
            direction={{ mobile: 'column', [mobileBreakpoint]: 'row' }}
            gap="20px"
          >
            {cardsData.map(({ icon, title, text }) => (
              <VStack
                key={title}
                minW={{ [mobileBreakpoint]: '480px', laptop: '640px' }}
                minH={{ [mobileBreakpoint]: '330px', laptop: '450px' }}
                bgColor="ourWhite"
                borderRadius={{ mobile: '24px', tablet: '32px', laptop: '48px' }}
                p={{ mobile: '24px', laptop: '48px' }}
                gap={{ mobile: '16px', laptop: '24px' }}
                textAlign="center"
              >
                <NextImage
                  src={icon}
                  alt="Иконка карточки"
                  w={{ mobile: '104px', laptop: '138px' }}
                  h="auto"
                />

                <Text
                  as="h3"
                  textStyle="h2"
                  fontSize={{ mobile: '26px', tablet: '36px', laptop: '48px' }}
                  color="ourBlack"
                  pt={{ tablet: '8px', laptop: 'unset' }}
                  px={{ tablet: '30px', laptop: '40px' }}
                >
                  {title}
                </Text>

                <InterText textStyle="b2" color="deepBlue">
                  {text}
                </InterText>
              </VStack>
            ))}
          </Stack>
        </Box>
      </Grid>
      {/* @ts-ignore */}
      <BlockShadow {...bottomShadowSameStyles} hideBelow={mobileBreakpoint} />
    </Box>
  );
};

export default AboutProgram;
